<template>
  <div>
    <v-container fluid>
      <v-row class="pb-1">
        <v-col cols="3">
          <date-range :from.sync="from" :to.sync="to" v-model="dateDisplay" @change="refresh()" option="Last 30 days" hide-details/>
        </v-col>
        <v-col cols="3">
          <multi-level-select label="Item" :menuData="itemsList" :first.sync="type" :second.sync="id" caption outlined @change="refresh()" hide-details no-validation/>
        </v-col>
        <v-col cols="2">
          <v-autocomplete label="Show customer" dense outlined :items="['Name','Type','Category']" v-model="by" hide-details @change="refresh()"/>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="printHtml('Sales by customer', report())"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="2">
          <v-select label="Source" hide-details outlined dense v-model="source" :items="[{text:'Shipments',value:'shipment'},{text:'Invoices',value:'invoice'},{text:'Sales orders',value:'salesOrder'}]"  @change="refresh()"/>
        </v-col>
        <v-col cols="2" offset="1">
          <v-switch label="Exclude $0 sales" hide-details dense class="mt-1" v-model="exclude0" @change="refresh()"/>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 228px)">
      <span v-html="report()"/>
    </v-card>
  </div>
</template>

<script>
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'
import createHierarchyList from '@/mixins/createHierarchyList'

export default {
  name: 'ReportsSalesByCustomer',
  mixins: [printHtml,createHierarchyList],
  data() {
    return {
      itemsList: [{name:'All',id:null},{divider:true}],
      type: null,
      id: null,
      by: 'Name',

      from: null,
      to: null,
      exclude0: false,
      source: 'shipment',
      dateDisplay: '',
      loading: true,
      totalRevenue: 0,
      totalCOG: 0,
      totalProfit: 0,
      totalMargin: 0,
      lines: []
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    }
  },
  async created() {
    this.$emit('name', 'Sales summary by customer')

    const productCategories = [];
    const itemCategories = [];
    const products = [];
    const items = [];
    const bundles = [];
    const spirits = [];
    const wines = [];
    const nonExcises = [];
    await Promise.all([
      this.$lazyListLoad(productCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Product'}]),
      this.$lazyListLoad(itemCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Item'}]),
      this.$lazyListLoad(products, 'products2', 'id,name,category{id}', 100, [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(items, 'items2', 'id,name,category{id}', 100, [{field:'active', comparator:'eq', value:'true'},{field:'sold', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(bundles, 'bundles2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(spirits, 'spirits2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(wines, 'wines2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(nonExcises, 'nonExcises2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}])
    ]);
    this.itemsList.push({name:'Product',id:'Product',menu:this.menuForProducts(products, productCategories)});
    if (bundles.length)
      this.itemsList.push({name:'Bundle',id:'Bundle',menu:bundles});
    if (items.length) {
      if (itemCategories.length) {
        const categories = this.createHierarchyList(itemCategories);
        for (const category of categories) {
          const sub = items.filter(item => item.category?.id === category.id);
          if (sub.length)
            category.menu = sub;
          else
            category.disabled = true;
        }
//          this.itemsList.push({name:'Item',id:'Item',menu:[...categories.filter(category => !category.disabled)]});
        this.itemsList.push({name:'Item',id:'Item',menu:categories});
        const leftOver = items.filter(item => !item.category?.id);
        if (leftOver.length)
          this.itemsList[this.itemsList.length-1].menu.push({divider:true},{name:'Other items',id:'x',menu:leftOver});
      }
      else
        this.itemsList.push({name:'Item',id:'Item',menu:items});
    }
    this.itemsList.push({name:'Spirit', id:'Spirit',menu:spirits});
    if (wines.length)
      this.itemsList.push({name:'Wine', id:'Wine',menu:wines});
    if (nonExcises.length)
      this.itemsList.push({name:'Non-excise', id:'NonExcise',menu:nonExcises});

    if (this.$route.query.from && this.$route.query.to) {
      this.from = this.$route.query.from;
      this.to = this.$route.query.to;
    }
  },
  methods: {
    menuForProducts(products, productCategories) {
      if (productCategories.length) {
        const categories = this.createHierarchyList([...productCategories]);
        for (const category of categories) {
          const sub = products.filter(product => product.category?.id === category.id);
          if (sub.length)
            category.menu = sub;
          else
            category.disabled = true;
        }
//        const items = categories.filter(category => !category.disabled)];
        const items = categories;
        const leftOver = products.filter(product => !product.category?.id);
        if (leftOver.length)
          items.push({divider:true},{name:'Other products',id:'x',menu:leftOver});
        return items;
      }
      else
        return products;
    },
    async refresh() {
      this.loading = true;
      const results = await this.$apiCall(`query ReportsSalesByCustomer($from:Date!,$to:Date!,$itemId:ID, $exclude0:Boolean, $source:String!, $by:String) {
                                              salesReportByCustomer(from:$from,to:$to,itemId:$itemId,exclude0:$exclude0,source:$source,by:$by) {
                                                name
                                                revenue,cog,profit,margin
                                              }
                                            }`,
                                          {from:this.from,to:this.to,itemId:this.type ? this.id : null,exclude0:this.exclude0,source:this.source,by:this.by});
      if (!results.error) {
        this.lines = results.data.salesReportByCustomer;

        this.totalRevenue = 0;
        this.totalCOG = 0;
        this.totalProfit = 0;
        for (const line of this.lines) {
          this.totalRevenue += line.revenue;
          this.totalCOG += line.cog;
          this.totalProfit += line.profit;
        }
        this.totalMargin = this.totalRevenue == 0 ? 0 : this.totalProfit / this.totalRevenue * 100;
      }
      this.loading = false;
    },

    report() {
      const lines = [
                  `<style>
                    @page {size:landscape}
                    .report {font-family: Roboto, sans-serif; width:100%;border-spacing:0;font-size:12px}
                    .report th {height:36px;padding:0 16px;vertical-align:middle}
                    .report .spacer {height:18px}
                    .report td {height:36px;padding:0 16px;vertical-align:middle}
                    .report .title {font-size:18px;text-transform:uppercase}
                    .report .sup-title {font-weight:normal;font-size:22px}
                    .report .sub-title {font-weight:normal;height:inherit;padding-bottom:20px;font-size:14px}
                    .report .header th {border-bottom:1px solid #000;margin-bottom:20px}
                    .report .text-left {text-align:left}
                    .report .text-right {text-align:right}
                    .report .font-weight-bold {font-weight:bold}
                    .report .total-row {font-weight:bold}
                  </style>
                   <table class="report">
                     <thead>
                        <tr>
                          <th colspan="6" class="sup-title">${this.company.name}</th>
                        </tr>
                        <tr>
                          <th colspan="6" class="title">Sales summary by customer</th>
                        </tr>
                        <tr>
                           <th colspan="6" class="sub-title">${this.dateDisplay}</th>
                        </tr>
                        <tr class="header">
                          <th width="32%" class="text-left">Customer ${this.by!=='Name'?this.by:''}</th>
                          <th width="17%" class="text-right">${this.source === 'salesOrder' ? 'Amount' : 'Revenue'}</th>
                          ${this.source === 'invoice' || this.source === 'salesOrder' ? ``
                                              : `<th width="17%" class="text-right">COG</th>
                                                 <th width="17%" class="text-right">Gross profit</th>
                                                 <th width="17%" class="text-right">Margin</th>`}
                        </tr>
                      </thead>
                      <tbody>`
      ];
      for (const line of this.lines) {
        lines.push(`<tr>
                    <td>${line.name}</td>
                    <td class="text-right">$${this.$number(line.revenue,2)}</td>`);
        if (this.source === 'shipment')
          lines.push(`<td class="text-right">$${this.$number(line.cog,2)}</td>
                      <td class="text-right">$${this.$number(line.profit,2)}</td>
                      <td class="text-right">${this.$number(line.margin,1)}%</td>`);
        lines.push(`</tr>`);
      }
      lines.push(`<tr class="total-row">
                  <td class="text-right">Total</td>
                  <td class="text-right">$${this.$number(this.totalRevenue,2)}</td>`);
      if (this.source === 'shipment')
        lines.push(`<td class="text-right">$${this.$number(this.totalCOG,2)}</td>
                    <td class="text-right">$${this.$number(this.totalProfit,2)}</td>
                    <td class="text-right">${this.$number(this.totalMargin,1)}%</td>`);
      lines.push(`</tr></tbody>`);
      lines.push(`</table>`);
      return lines.join('');
    },

    exportCSV() {
      const lines = [ `"${this.company.name}"`,
                      `"Sales by customer"`,
                      `"${this.dateDisplay}"`
                    ];
      lines.push(this.source !== 'shipment' ? `"Customer ${this.by!=='Name'?this.by:''}","Revenue"` : `"Customer ${this.by!=='Name'?this.by:''}","Revenue","COG","Gross profit","Margin"`);
      for (const line of this.lines) {
        if (this.source !== 'shipment')
          lines.push(`"${line.name}","${this.$number(line.revenue,2)}"`);
        else
          lines.push(`"${line.name}","${this.$number(line.revenue,2)}","${this.$number(line.cog,2)}","${this.$number(line.profit,2)}","${this.$number(line.margin,1)}"`);
      }

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, 'Sales by customer.csv');
    }
  }
}
</script>